import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForwardRounded, SearchRounded, CloseRounded } from "@mui/icons-material";
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import { useAuth } from "app/providers/authContext";
import { findWelcomeModules, welcomeModules, welcomeName } from "./welcomeModel";
import WelcomeHero from "./WelcomeHero";
import WelcomeHelp from "./WelcomeHelp";
import ModuleCard from "./ModuleCard";
import { panelSx, focusSx } from "./welcomeStyles";

export default function Dashboard() {
  const auth = useAuth();
  const modules = welcomeModules(auth);
  const name = welcomeName(auth.user);
  const role = auth.user?.role?.name || auth.user?.user?.role?.name;
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("");
  const [limit, setLimit] = useState(6);
  const areas = [...new Set(modules.map((item) => item.area))];
  const selectedArea = areas.includes(area) ? area : "";
  const results = findWelcomeModules(modules, query, selectedArea);
  useEffect(() => setLimit(6), [query, selectedArea]);
  const today = useMemo(
    () =>
      new Intl.DateTimeFormat("es-CO", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "America/Bogota",
      }).format(new Date()),
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        component="main"
        sx={{ py: { xs: 2, md: 3 }, "--welcome-accent": "#00e400", "--welcome-ink": "#0d3129" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2.5,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              component="p"
              sx={{
                color: "text.secondary",
                fontSize: 12,
                letterSpacing: 1.4,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              Inicio / Tu espacio de trabajo
            </Typography>
            <Typography
              component="h1"
              sx={{ color: "text.primary", fontSize: { xs: 22, md: 28 }, fontWeight: 700, mt: 0.5 }}
            >
              {name ? `Hola, ${name}` : "Bienvenido a Goper"}
              <Box component="span" sx={{ color: "info.main" }}>
                .
              </Box>
            </Typography>
          </Box>
          <Typography
            component="p"
            sx={{ fontSize: 12, color: "text.secondary", textTransform: "none" }}
          >
            {today.charAt(0).toLocaleUpperCase("es") + today.slice(1)}
            <Box
              component="span"
              sx={{ display: "block", fontSize: 11, textAlign: { sm: "right" }, mt: 0.25 }}
            >
              Hora de Colombia
            </Box>
          </Typography>
        </Box>

        <WelcomeHero role={role} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 290px" },
            gap: 3,
            mt: 4,
            alignItems: "start",
          }}
        >
          <Box
            component="section"
            id="mis-modulos"
            aria-labelledby="modules-title"
            sx={{ scrollMarginTop: 100, minWidth: 0 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.75,
                gap: 2,
              }}
            >
              <Typography
                component="h2"
                id="modules-title"
                sx={{ color: "text.primary", fontSize: 21, fontWeight: 700, letterSpacing: -0.4 }}
              >
                ¿Qué necesitas hacer hoy?
              </Typography>
              {!auth.isLoading && (
                <Chip
                  label={`${modules.length} ${modules.length === 1 ? "módulo" : "módulos"}`}
                  size="small"
                  sx={{ color: "text.secondary", fontSize: 11, bgcolor: "action.hover" }}
                />
              )}
            </Box>
            <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 2.5 }}>
              Busca una herramienta o explora tus áreas de trabajo.
            </Typography>
            <TextField
              fullWidth
              label="Buscar un módulo"
              placeholder="Escribe el nombre de un módulo…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 52,
                  borderRadius: 2.5,
                  bgcolor: "background.paper",
                  fontSize: 14,
                },
                "& .MuiInputLabel-root": { fontSize: 14 },
              }}
            />
            {areas.length > 1 && (
              <Box
                role="group"
                aria-label="Filtrar por área"
                sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}
              >
                {["", ...areas].map((value) => (
                  <Button
                    key={value}
                    aria-pressed={selectedArea === value}
                    onClick={() => setArea(value)}
                    sx={{
                      "&&": {
                        minWidth: 0,
                        px: 1.5,
                        py: 0.7,
                        minHeight: 34,
                        borderRadius: 2,
                        fontSize: 11,
                        textTransform: "none",
                        fontWeight: 600,
                        bgcolor: selectedArea === value ? "info.main" : "background.paper",
                        color: selectedArea === value ? "#fff" : "text.secondary",
                        border: "1px solid",
                        borderColor: selectedArea === value ? "info.main" : "divider",
                      },
                      ...focusSx,
                    }}
                  >
                    {value || "Todas las áreas"}
                  </Button>
                ))}
              </Box>
            )}
            {auth.isLoading ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mt: 2.5,
                }}
                aria-label="Cargando tus módulos"
              >
                {[0, 1, 2, 3].map((key) => (
                  <Skeleton key={key} variant="rounded" height={160} />
                ))}
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mt: 2.5,
                  }}
                >
                  {results.slice(0, limit).map((module) => (
                    <ModuleCard module={module} key={module.key} />
                  ))}
                </Box>
                {!results.length && (
                  <Paper sx={{ ...panelSx, textAlign: "center", p: 4, mt: 2.5 }}>
                    <SearchRounded
                      sx={{ color: "text.secondary", fontSize: "32px !important", mb: 1 }}
                    />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>
                      {modules.length ? "No encontramos ese módulo" : "Tus accesos aparecerán aquí"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
                      {modules.length
                        ? "Prueba con otro nombre o cambia el área seleccionada."
                        : "Si necesitas acceso a un módulo, consulta con el administrador."}
                    </Typography>
                    {modules.length > 0 && (
                      <Button
                        startIcon={<CloseRounded />}
                        onClick={() => {
                          setQuery("");
                          setArea("");
                        }}
                        sx={{ "&&": { color: "info.main", textTransform: "none", mt: 2 } }}
                      >
                        Limpiar búsqueda
                      </Button>
                    )}
                  </Paper>
                )}
                {results.length > limit && (
                  <Button
                    fullWidth
                    onClick={() => setLimit((value) => value + 6)}
                    endIcon={<ArrowForwardRounded />}
                    sx={{
                      "&&": {
                        minHeight: 44,
                        mt: 2,
                        color: "text.primary",
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                      },
                    }}
                  >
                    Ver más módulos ({results.length - limit})
                  </Button>
                )}
              </>
            )}
          </Box>

          <WelcomeHelp hasModules={modules.length > 0} />
        </Box>
      </Box>
    </DashboardLayout>
  );
}
